import moment, { Moment } from 'moment-timezone';
import { format } from 'date-fns';
import { DayOfWeek, TimeslotFrequencyInput } from 'globalTypes';

export type RecurringFrequencyInput = Exclude<TimeslotFrequencyInput, 'once'>;

export default function dateTimeFormat(dateString: Date, options?: any): string {
  return new global.Intl.DateTimeFormat('en-us', options).format(dateString);
}

// returns a string formatted like this: Thu, Dec 17th at 12:00 PM
export function userReadableDateTimeFormat(date: Date | string) {
  return format(date, 'ddd, MMM Do [at] h:mm A');
}

// returns a string formatted like this: Thu, Nov 19 at 7:00PM (PDT)
// using the timezone moment thinks the user is in
export function userReadableDateTimeFormatLocalTZ(dateTime: string) {
  return moment(dateTime)
    .tz(moment.tz.guess())
    .format('ddd[,] MMM DD [at] LT [(]z[)]');
}

// returns a string formatted like this: Thursday at 7:00PM PDT
// using the timezone moment thinks the user is in
export function userReadableDayAndTimeFormatLocalTZ(dateTime: string) {
  return moment(dateTime)
    .tz(moment.tz.guess())
    .format('dddd [at] LT z');
}

// returns a string formatted like this: Thu, Dec 17
export function userReadableDateFormat(date: Date | string) {
  return format(date, 'ddd, MMM D');
}

// returns a string formatted like this: Aug 17
export function abbreviatedMonthAndDayFormat(date: Date | string) {
  return format(date, 'MMM D');
}

// returns a string formatted like this: August 17
export function fullMonthAndDayFormat(date: Date | string) {
  return format(date, 'MMMM D');
}

// returns a string formatted like this: 2021-07-19
// given an atomic date and optionally a timezone.
// if no timezone is given, assumes the given Date is already on the correct calendar date.
export function dateOnlyFormat(date: Date | string, timezone?: string) {
  return timezone ? moment.tz(date, timezone).format('YYYY-MM-DD') : format(date, 'YYYY-MM-DD');
}

// takes string formatted like this: 2021-07-19
// and returns a DayOfWeek
export function dateOnlyFormatToDayOfWeek(date: string): DayOfWeek {
  const dayOfWeekString = moment(date).format('dddd') as keyof typeof DayOfWeek;
  return DayOfWeek[dayOfWeekString];
}

export function mapRecurringFrequencyToInterval(frequency: RecurringFrequencyInput) {
  if (frequency === 'weekly') return 1;
  else if (frequency === 'everyOtherWeek') return 2;
  return undefined;
}

export function formatRecurrenceRuleDate(date: Date): string {
  return moment.utc(date).format('YYYYMMDDTHHmmss[Z]');
}

export function convertRecurrenceRuleDateToUserReadableString(date: string): string {
  return userReadableDateTimeFormat(moment.utc(date, 'YYYYMMDDTHHmmss[Z]').toDate());
}

// takes a list of recurrence rules, finds the one with exception dates,
// and converts them to an array of user readable strings
export function extractExceptionDatesToUserReadableStrings(recurrenceRules: string[]): string[] {
  const exceptionDateString = recurrenceRules.find(rule => rule.includes('EXDATE'));
  if (!exceptionDateString) return [];

  // EXDATE:20201224T000000Z,20201224T000000Z -> ["EXDATE","20201224T000000Z,20201224T000000Z"]
  const [_, datesString] = exceptionDateString.split(':');
  // "20201224T000000Z,20201224T000000Z" -> ["20201224T000000Z","20201224T000000Z"]
  const dates = datesString.split(',');
  return dates.map(d => convertRecurrenceRuleDateToUserReadableString(d));
}

export function formatWeeklyRecurrenceRule(interval: number, until?: Date | string) {
  const weeklyRecurrenceRuleSet = ['RRULE:FREQ=WEEKLY'];
  weeklyRecurrenceRuleSet.push(`INTERVAL=${interval}`);
  if (until) {
    weeklyRecurrenceRuleSet.push(`UNTIL=${formatRecurrenceRuleDate(new Date(until))}`);
  }
  return weeklyRecurrenceRuleSet.join(';');
}

export function formatExceptionDateRecurrenceRule(exceptionDates: (Date | string)[]) {
  if (exceptionDates.length === 0) return '';
  return `EXDATE:${exceptionDates.map(date => formatRecurrenceRuleDate(new Date(date))).join(',')}`;
}

export function formatTimeForCoordinator(dateTime: string | Moment) {
  const LATime = moment(dateTime).tz('America/Los_Angeles');

  return `${LATime.format('h:mm A z')}`;
}

export function formatRecurrenceRules(params: {
  interval: number;
  until?: Date | string;
  exceptionDates?: (Date | string)[];
  excludeUnavailableDates: boolean;
}) {
  const weeklyRecurrenceRule = formatWeeklyRecurrenceRule(params.interval, params.until);

  if (params.excludeUnavailableDates && params.exceptionDates && params.exceptionDates.length > 0) {
    const exceptionDateRecurrenceRule = formatExceptionDateRecurrenceRule(params.exceptionDates);
    return [weeklyRecurrenceRule, exceptionDateRecurrenceRule];
  }
  return [weeklyRecurrenceRule];
}

/** Formats a time in HH:mm format to Los Angeles time. Does the formatting using
 * relative the current date. This makes no difference when the coach's timezone
 * also uses DST, but if it doesn't can result in different dates depending on the date of year.
 */
export function formatAvailabilityForCoordinator(timeOfDay: string, coachTimezone: string) {
  const localCoachTime = moment(timeOfDay, 'HH:mm').tz(coachTimezone, true);
  const pacificTime = localCoachTime.tz('America/Los_Angeles');
  const formattedTime = `${pacificTime.format('h:mm A z')}`;
  return formattedTime;
}

/** formats a date using PT time */
export function formatDateForCoordinator(date: string | Moment | Date, dateFormat: string) {
  return moment(date)
    .tz('America/Los_Angeles')
    .format(dateFormat);
}
